// Delivery Tracking — filtered view of orders in shipped / delivered / pod_confirmed
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import AdvanceOrderModal from '../../components/dme/AdvanceOrderModal';
import { format } from 'date-fns';
import { StatTile } from '../../components/StatTile';
import DataTable from '../../components/DataTable';

const DELIVERY_STATUSES = ['supplier_ordered', 'shipped', 'delivered', 'pod_confirmed'];

const STATUS_LABELS = {
  supplier_ordered: { label: 'Supplier Ordered', color: 'var(--info)',    bg: 'var(--info-bg)' },
  shipped:          { label: 'Shipped',           color: 'var(--info)',    bg: 'var(--info-bg)' },
  delivered:        { label: 'Delivered',         color: 'var(--info)',    bg: 'var(--info-bg)' },
  pod_confirmed:    { label: 'POD Confirmed',     color: 'var(--success)', bg: 'var(--success-bg)' },
};

const nextDeliveryStatus = (s) => {
  const seq = ['supplier_ordered', 'shipped', 'delivered', 'pod_confirmed'];
  const i = seq.indexOf(s);
  return i >= 0 && i < seq.length - 1 ? seq[i + 1] : null;
};


export default function Delivery() {
  const [orders, setOrders]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('dme_orders')
      .select('*')
      .in('status', DELIVERY_STATUSES)
      .eq('is_test', false)
      .order('updated_at', { ascending: false });
    setOrders(data || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = orders.filter(o => {
    const q = search.toLowerCase();
    if (q && !o.client_name?.toLowerCase().includes(q) && !o.order_ref?.toLowerCase().includes(q)) return false;
    if (filterStatus && o.status !== filterStatus) return false;
    return true;
  });

  const statCounts = {
    ordered:   orders.filter(o => o.status === 'supplier_ordered').length,
    shipped:   orders.filter(o => o.status === 'shipped').length,
    delivered: orders.filter(o => o.status === 'delivered').length,
    pod:       orders.filter(o => o.status === 'pod_confirmed').length,
  };

  const inputStyle = { width: '100%', padding: '8px 10px', fontSize: 12, borderRadius: 7, border: '0.5px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', fontFamily: 'inherit', boxSizing: 'border-box' };
  // Product category is a parallel identifier, not a status — neutral for all.
  const catColor   = () => 'var(--text3)';
  const catBg      = () => 'var(--bg2)';

  const columns = [
    {
      accessorKey: 'order_ref', header: 'Order Ref', size: 110,
      meta: { mono: true, mobile: 'secondary' },
      cell: info => info.getValue() || '—',
    },
    {
      accessorKey: 'client_name', header: 'Patient', size: 150,
      meta: { mobile: 'primary' },
      cell: info => info.getValue(),
    },
    {
      id: 'category', accessorKey: 'product_category', header: 'Category', size: 110,
      meta: { mobile: 'secondary' },
      cell: info => {
        const cat = info.getValue();
        return <span style={{ fontSize: 10, fontWeight: 500, padding: '2px 7px', borderRadius: 20, background: catBg(cat), color: catColor(cat), textTransform: 'capitalize' }}>{cat}</span>;
      },
    },
    {
      accessorKey: 'hcpcs_code', header: 'HCPCS', size: 90,
      meta: { mono: true, mobile: 'hidden' },
      cell: info => info.getValue() || '—',
    },
    {
      accessorKey: 'supplier_name', header: 'Supplier', size: 130,
      meta: { mobile: 'hidden' },
      cell: info => <span style={{ color: 'var(--text3)' }}>{info.getValue() || '—'}</span>,
    },
    {
      id: 'stage', accessorKey: 'status', header: 'Stage', size: 130,
      meta: { mobile: 'primary' },
      cell: info => {
        const sl = STATUS_LABELS[info.getValue()] || {};
        return <span style={{ fontSize: 10, fontWeight: 500, padding: '2px 8px', borderRadius: 20, background: sl.bg, color: sl.color, whiteSpace: 'nowrap' }}>{sl.label}</span>;
      },
    },
    {
      accessorKey: 'tracking_number', header: 'Tracking #', size: 130,
      meta: { mono: true, mobile: 'secondary' },
      cell: info => info.getValue() || '—',
    },
    {
      accessorKey: 'shipped_date', header: 'Shipped', size: 100,
      meta: { mobile: 'hidden' },
      cell: info => info.getValue() ? format(new Date(info.getValue()), 'MMM d') : '—',
    },
    {
      accessorKey: 'delivered_date', header: 'Delivered', size: 100,
      meta: { mobile: 'hidden' },
      cell: info => info.getValue() ? format(new Date(info.getValue()), 'MMM d') : '—',
    },
    {
      id: 'actions', header: '', size: 110, enableSorting: false,
      meta: { mobile: 'secondary' },
      cell: info => {
        const o = info.row.original;
        return nextDeliveryStatus(o.status) ? (
          <button className="btn btn-outline btn-sm" style={{ fontSize: 10, padding: '3px 9px' }}
            onClick={e => { e.stopPropagation(); setSelected(o); }}>
            Update →
          </button>
        ) : null;
      },
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--text)' }}>Delivery Tracking</div>
        <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>Orders in transit — from supplier ordered through POD confirmation</div>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,minmax(0,1fr))', gap: 8, marginBottom: 14 }}>
        {[
          { label: 'Supplier Ordered', val: statCounts.ordered },
          { label: 'Shipped',          val: statCounts.shipped },
          { label: 'Delivered',        val: statCounts.delivered },
          { label: 'POD Confirmed',    val: statCounts.pod },
        ].map(s => (
          <StatTile key={s.label} label={s.label} value={s.val} compact />
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search patient or order ref…"
          style={{ ...inputStyle, flex: '1 1 200px', maxWidth: 300 }} />
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ ...inputStyle, flex: '0 0 auto', width: 'auto' }}>
          <option value="">All delivery stages</option>
          {DELIVERY_STATUSES.map(s => <option key={s} value={s}>{STATUS_LABELS[s]?.label}</option>)}
        </select>
      </div>

      <div className="card">
        {!loading && filtered.length === 0 ? (
          <div className="empty-state" style={{ padding: '60px 20px' }}>
            <div style={{ fontSize: 32, marginBottom: 10 }}>🚚</div>
            <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)', marginBottom: 6 }}>No deliveries in progress</div>
            <div style={{ fontSize: 12, color: 'var(--text3)' }}>Orders advance here once marked as "Supplier Ordered" from Equipment Orders.</div>
          </div>
        ) : (
          <DataTable
            tableId="dme-delivery"
            columns={columns}
            data={filtered}
            getRowId={o => o.id}
            onRowClick={setSelected}
            loading={loading}
            emptyMessage="No orders match your filters."
            maxHeight="calc(100vh - 260px)"
          />
        )}
      </div>

      {/* Update modal */}
      {/* Every delivery stage change goes through the gate: tracking for
          Shipped, a date for Delivered, and a real proof-of-delivery record —
          who received it, when, how many, and the signed slip — for POD. */}
      {selected && (
        <AdvanceOrderModal order={selected} onClose={() => setSelected(null)} onAdvanced={() => { setSelected(null); load(); }} />
      )}
    </div>
  );
}
